/**
 * Dialog shown to the borrower when returning an item.
 * Allows optional condition recording (good / minor wear / damaged) and notes.
 * Condition data is stored on the loan and visible to the owner in loan history.
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { useInitiateReturn, useConfirmReturn } from '@/hooks/useLoans';
import { useNetworkStatusContext } from '@/contexts/NetworkStatusContext';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';

type ReturnCondition = 'good' | 'minor_wear' | 'damaged';

interface ConfirmReturnDialogProps {
  loanId: string | null;
  itemName?: string;
  mode: 'initiate' | 'confirm';
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CONDITION_OPTIONS: { value: ReturnCondition; label: string; bg: string; text: string; border: string }[] = [
  { value: 'good', label: 'Good', bg: T.successSoft, text: T.success, border: T.successBorder },
  { value: 'minor_wear', label: 'Minor wear', bg: T.warningSoft, text: T.warning, border: T.warningBorder },
  { value: 'damaged', label: 'Damaged', bg: T.dangerSoft, text: T.danger, border: T.dangerBorder },
];

export function ConfirmReturnDialog({
  loanId,
  itemName,
  mode,
  open,
  onOpenChange,
}: ConfirmReturnDialogProps) {
  const initiateReturn = useInitiateReturn();
  const confirmReturn = useConfirmReturn();
  const { isOnline } = useNetworkStatusContext();

  const [condition, setCondition] = useState<ReturnCondition | null>(null);
  const [notes, setNotes] = useState('');

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setCondition(null);
      setNotes('');
    }
  }, [open]);

  const mutation = mode === 'initiate' ? initiateReturn : confirmReturn;
  const isPending = mutation.isPending;
  const disabled = isPending || !isOnline;

  const handleSubmit = async () => {
    if (!loanId) return;
    try {
      await mutation.mutateAsync({
        loanId,
        condition: condition ?? undefined,
        notes: notes.trim() || undefined,
      });
      posthog.capture('loan_returned_with_condition', {
        loan_id: loanId,
        condition: condition ?? 'not_recorded',
        has_notes: !!notes.trim(),
      });
      toast.success(mode === 'initiate' ? 'Return initiated' : 'Return confirmed');
      onOpenChange(false);
    } catch {
      // Error toast handled by hook onError
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Returning {itemName ?? 'item'}</DialogTitle>
        </DialogHeader>

        <p style={{ fontSize: 14, color: T.textSec, margin: '4px 0 16px' }}>
          Optionally note the item's condition — the owner will see this.
        </p>

        {/* Condition toggle buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {CONDITION_OPTIONS.map((opt) => {
            const selected = condition === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCondition(selected ? null : opt.value)}
                disabled={isPending}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  borderRadius: RADIUS.md,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: isPending ? 'not-allowed' : 'pointer',
                  background: selected ? opt.bg : T.surfaceSubtle,
                  color: selected ? opt.text : T.textMuted,
                  border: selected ? `1px solid ${opt.border}` : `1px solid ${T.border}`,
                  transition: 'all 0.15s ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Notes textarea — progressive disclosure */}
        {condition && (
          <div style={{ marginBottom: 16 }}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={200}
              placeholder="Any notes for the owner... (optional)"
              disabled={isPending}
              rows={3}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: RADIUS.md,
                border: `1px solid ${T.border}`,
                background: T.surfaceSubtle,
                color: T.text,
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
              }}
            />
            {notes.length > 0 && (
              <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'right', marginTop: 4 }}>
                {notes.length}/200
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              fontSize: 14,
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={disabled}
            title={!isOnline ? "You're offline" : undefined}
            style={{
              padding: '10px 18px',
              borderRadius: RADIUS.md,
              background: disabled ? T.surfaceSubtle : T.success,
              color: disabled ? T.textMuted : T.white,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: disabled ? 'none' : SHADOW.btn.success,
            }}
          >
            Confirm Return
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
